// bridge.js — sottomenù PROXY per i moduli legacy (design §6.2, innesto 5 — R4).
// Il modulo NON si tocca (contabilita-module è frozen, admin-module è off-limits):
// ogni voce del sottomenù shell è un bridgeEntry DICHIARATIVO su un bersaglio DOM
// della nav interna; il click della shell diventa un click programmatico sul
// bersaglio (stesso pattern proxyRail già in produzione, wsh:173-177).
//
// Regole (innesto 5 — MAI proxy silenzioso):
//  - assertBridge al mount: OGNI bersaglio deve risolvere; uno rotto ⇒ fallback
//    AUTOMATICO a submenu:'internal' (Workspaces.update) + toast + console.error.
//  - click su bersaglio assente ⇒ toast "Voce non disponibile", mai no-op.
//
// Bersaglio (target) = { selector, index?, text? }:
//  - selector: CSS. index: posizione nella NodeList (default 0) — serve a cont,
//    i cui .nav-item non hanno attributi identificativi: la POSIZIONE nella lista
//    piatta è stabile anche a sidebar collassata (le intestazioni di gruppo non
//    sono .nav-item).
//  - text: guardia anti-deriva — se il nodo HA testo, deve CONTENERE text (le
//    label del glossario = le label interne del modulo). Nodo senza testo (es.
//    sidebar cont collassata: solo icone) ⇒ il vincolo di conteggio/selettore
//    resta l'unica guardia (dichiarato).
//
// Puro dove possibile (resolveTarget/assertBridge/clickBridge: node --test in
// server/src/lib/shell-bridge.test.js); il solo stato vive in installBridgeGuard.

import { label } from './glossary.js';

export function bridgeEntry(id, glossKey, target, opts) {
  opts = opts || {};
  return {
    id,
    glossKey,
    badge: opts.badge || null,
    adminOnly: !!opts.adminOnly,
    target: typeof target === 'string' ? { selector: target } : target,
  };
}

// nav[] del contratto v2 (§3) a partire dalle sezioni di bridgeEntry: al registry
// arrivano SOLO i campi del contratto (il target resta negli entries dell'adapter).
export function bridgeNav(sections) {
  return sections.map((s) => ({
    section: s.section,
    items: s.items.map((e) => ({ id: e.id, glossKey: e.glossKey, badge: e.badge, adminOnly: e.adminOnly })),
  }));
}

export function flatEntries(sections) {
  return sections.reduce((acc, s) => acc.concat(s.items), []);
}

// Risolve un target → { el, error: null | 'selector' | 'missing' | 'text' }.
export function resolveTarget(doc, target) {
  let els;
  try {
    els = doc.querySelectorAll(target.selector);
  } catch (e) {
    return { el: null, error: 'selector' };
  }
  const el = els[target.index || 0] || null;
  if (!el) return { el: null, error: 'missing' };
  if (target.text) {
    const txt = (el.textContent || '').trim();
    if (txt && txt.indexOf(target.text) === -1) return { el: null, error: 'text' };
  }
  return { el, error: null };
}

// assertBridge (§6.2): OGNI voce deve risolvere → { ok, broken: [{id, selector, error}] }.
// extraChecks(doc) opzionale → array di broken aggiuntivi (es. conteggio esatto
// dei .nav-item di cont: una voce in più/in meno = ordine non più affidabile).
export function assertBridge(doc, entries, extraChecks) {
  const broken = [];
  for (const e of entries) {
    const r = resolveTarget(doc, e.target);
    if (!r.el) broken.push({ id: e.id, selector: e.target.selector, error: r.error });
  }
  if (typeof extraChecks === 'function') {
    try {
      const extra = extraChecks(doc);
      if (Array.isArray(extra)) broken.push(...extra);
    } catch (err) {
      broken.push({ id: '(extraChecks)', selector: '-', error: String(err && err.message) });
    }
  }
  return { ok: broken.length === 0, broken };
}

// Click immediato → 'ok' | 'unknown' (voce non censita) | 'missing' (bersaglio assente).
export function clickBridge(doc, entries, itemId) {
  const e = entries.find((x) => x.id === itemId);
  if (!e) return 'unknown';
  const r = resolveTarget(doc, e.target);
  if (!r.el) return 'missing';
  r.el.click();
  return 'ok';
}

// Click con RIPROVA breve (il modulo può stare ancora montando: cont carica le
// dipendenze in async al primo onShow). Fallisce RUMOROSAMENTE: toast + error.
export function clickBridgeRetry(win, entries, itemId, opts) {
  opts = opts || {};
  const bus = win.Workspaces && win.Workspaces.bus;
  const entry = entries.find((x) => x.id === itemId);
  const itemLabel = entry ? label(entry.glossKey) : itemId;
  const pollMs = opts.pollMs || 250;
  const timeoutMs = opts.timeoutMs || 3000;

  function fail() {
    console.error('[bridge] click fallito su "' + itemId + '" (' + (entry ? entry.target.selector : 'voce non censita') + ')');
    if (bus) {
      try { bus.emit('toast', { kind: 'error', msg: label('shell.bridge.missing') + itemLabel }); } catch (e) { console.error(e); }
    }
    if (opts.onFail) opts.onFail();
  }

  if (!entry) { fail(); return false; }
  if (clickBridge(win.document, entries, itemId) === 'ok') return true;
  const t0 = Date.now();
  const t = win.setInterval(() => {
    if (clickBridge(win.document, entries, itemId) === 'ok') { win.clearInterval(t); return; }
    if (Date.now() - t0 > timeoutMs) { win.clearInterval(t); fail(); }
  }, pollMs);
  return true; // presa in carico (la riprova è in corso); il fallimento resta rumoroso
}

// Stile una-sola-volta (pattern injectWshStyle): usato dagli adapter per il CSS
// scoped che nasconde la nav interna SOLO quando il sottomenù shell è attivo
// (chiavi body.shell-submenu + body.wsh-<id>/workspace-amm: classe via host).
export function injectStyleOnce(doc, id, css) {
  if (doc.getElementById(id)) return;
  const st = doc.createElement('style');
  st.id = id;
  st.textContent = css;
  doc.head.appendChild(st);
}

// installBridgeGuard — l'assert "al mount" (§6.2) con la vita reale dei legacy:
// la nav interna compare in ASYNC (cont monta React al primo onShow; amm-chrome
// nasce col token). A ogni ingresso nel modulo (shell:module-changed, replay per
// l'ingresso di boot già avvenuto):
//  - primo ingresso: attende ready(doc) (poll) e poi asserisce;
//  - ingressi successivi a bridge verde: ri-assert immediato (economico, anti-deriva);
//  - assert ROSSO o timeout ⇒ fallback(submenu:'internal') + toast + console.error, UNA volta.
// Ritorna l'unsubscribe del listener bus.
export function installBridgeGuard(win, cfg) {
  const W = win.Workspaces;
  const bus = W.bus;
  const pollMs = cfg.pollMs || 300;
  const timeoutMs = cfg.timeoutMs || 15000;
  let status = 'idle'; // idle → checking → ok | fallback
  let timer = null;

  function fallback(broken) {
    if (status === 'fallback') return;
    status = 'fallback';
    if (timer) { win.clearInterval(timer); timer = null; }
    try { W.update(cfg.moduleId, { submenu: 'internal' }); } catch (e) { console.error('[bridge] fallback update', e); }
    console.error('[bridge] ' + cfg.moduleId + ': assertBridge ROSSO → sottomenù interno (fallback). Dettaglio:', JSON.stringify(broken));
    try { bus.emit('toast', { kind: 'error', msg: cfg.moduleLabelText + label('shell.bridge.fallback') }); } catch (e) { console.error(e); }
  }

  function assertNow() {
    const res = assertBridge(win.document, cfg.entries, cfg.extraChecks);
    if (res.ok) {
      status = 'ok';
      if (cfg.onActive) { try { cfg.onActive(); } catch (e) { console.error('[bridge] onActive', e); } }
    } else {
      fallback(res.broken);
    }
  }

  function checkOnEnter() {
    if (status === 'fallback' || status === 'checking') return;
    if (status === 'ok') { assertNow(); return; }
    status = 'checking';
    const t0 = Date.now();
    timer = win.setInterval(() => {
      if (cfg.ready(win.document)) {
        win.clearInterval(timer); timer = null;
        assertNow();
      } else if (Date.now() - t0 > timeoutMs) {
        win.clearInterval(timer); timer = null;
        fallback([{ id: '(mount)', selector: '(nav interna mai comparsa in ' + timeoutMs + 'ms)', error: 'timeout' }]);
      }
    }, pollMs);
  }

  // Copertura del RESTORE senza evento: amm si auto-ripristina da admin-module
  // (setWorkspace da localStorage, nessun click sul rail ⇒ nessun
  // shell:module-changed). Finché il guard è 'idle', un poll leggero controlla
  // activeNow() (classe body) e innesca la verifica; si spegne al primo verdetto.
  if (typeof cfg.activeNow === 'function') {
    const idlePoll = win.setInterval(() => {
      if (status !== 'idle') { win.clearInterval(idlePoll); return; }
      let on = false;
      try { on = !!cfg.activeNow(); } catch (e) { /* body non pronto */ }
      if (on) checkOnEnter();
    }, 1000);
  }

  return bus.on('shell:module-changed', (p) => {
    if (p && p.id === cfg.moduleId) checkOnEnter();
  }, { replay: true });
}
